/* ──────────────────────────────────────────────────────────────────────────
 * Advisor Marketplace — Experts Directory (Discover).
 *
 * Reached from the "Discover" step on the Advisor Marketplace. Lists every IPO
 * expert registered on the platform (live data from the matchmaker backend:
 * GET /api/matchmaker/experts/browse), reusing the Advisor Marketplace
 * (`.adv-mp-root`) UI/UX. Adds a Google-style review experience per expert
 * (front-end demo — reviews show instantly, not persisted).
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./AdvisorMarketplace.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AV_COLORS = [
  { bg: "#E6F1FB", c: "#0C447C" },
  { bg: "#E1F5EE", c: "#085041" },
  { bg: "#EEEDFE", c: "#3C3489" },
  { bg: "#FAEEDA", c: "#633806" },
];

const CHIP_DEFS = [
  { id: "all", label: "All roles", match: null },
  { id: "ca", label: "CA / Auditor", match: "Chartered Accountant" },
  { id: "legal", label: "Legal", match: "Legal Advisors" },
  { id: "brlm", label: "Merchant Banker", match: "Merchant Banker" },
  { id: "rta", label: "Registrar (RTA)", match: "Registrar & Transfer" },
  { id: "val", label: "Valuation", match: "Registered Valuation" },
  { id: "cs", label: "Company Secretary", match: "Company Secretary" },
];

/* Deterministic arbitrary seed reviews so every expert has a Google-style history. */
const REVIEWERS = [
  "Rohan Mehta", "Sneha Kapoor", "Vikram Joshi", "Priya Nair", "Arjun Rao",
  "Kavya Menon", "Aditya Shah", "Meera Pillai", "Sanjay Gupta", "Tara Verma",
];
const SNIPPETS = [
  { t: "Exceptional guidance through our DRHP filing — responsive and deeply versed in SEBI norms.", r: 5, d: "2 weeks ago" },
  { t: "Helped us close our SME IPO ahead of schedule. Highly professional throughout.", r: 5, d: "1 month ago" },
  { t: "Solid expertise, though turnaround could be a touch faster during peak filing season.", r: 4, d: "3 weeks ago" },
  { t: "Very thorough with compliance and documentation. Would gladly engage again.", r: 5, d: "2 months ago" },
  { t: "Clear communication and a fair fee structure. A reliable advisor overall.", r: 4, d: "1 week ago" },
  { t: "Brought real strategic value to our pre-IPO restructuring. Strongly recommend.", r: 5, d: "5 days ago" },
  { t: "Knowledgeable; we had a couple of scheduling hiccups early on, resolved well.", r: 4, d: "6 weeks ago" },
  { t: "Sharp on valuation and peer benchmarking. Made our board comfortable quickly.", r: 5, d: "3 days ago" },
];

const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const seedReviews = (expertId) => {
  const h = hashStr(expertId);
  const count = 2 + (h % 3); // 2–4 reviews
  const out = [];
  for (let i = 0; i < count; i++) {
    const snip = SNIPPETS[(h + i * 3) % SNIPPETS.length];
    out.push({
      name: REVIEWERS[(h + i * 5) % REVIEWERS.length],
      r: snip.r,
      t: snip.t,
      d: snip.d,
    });
  }
  return out;
};

const Stars = ({ value, className = "stars" }) => {
  const full = Math.round(value);
  return <span className={className}>{"★★★★★".slice(0, full)}{"☆☆☆☆☆".slice(0, 5 - full)}</span>;
};

const ExpertCard = ({ expert, idx, expanded, onToggle, reviews, onAddReview }) => {
  const color = AV_COLORS[idx % AV_COLORS.length];
  const initials = (expert.full_name || "?").trim().slice(0, 2).toUpperCase();
  const role = expert.expertise_labels?.[0] || "IPO Advisor";
  const avg = reviews.length ? reviews.reduce((a, b) => a + b.r, 0) / reviews.length : 0;

  const [pick, setPick] = useState(5);
  const [name, setName] = useState("");
  const [text, setText] = useState("");

  const submit = () => {
    if (!text.trim()) {
      toast.error("Please write a short review before posting.");
      return;
    }
    onAddReview(expert.expert_id, { name: name.trim() || "Anonymous", r: pick, t: text.trim(), d: "Just now" });
    setText("");
    setName("");
    setPick(5);
    toast.success("Review posted");
  };

  return (
    <div className="advisor-card" data-testid={`expert-card-${expert.expert_id}`}>
      <div className="ac-top">
        <div className="ac-logo" style={{ background: color.bg, color: color.c }}>{initials}</div>
        <div className="ac-meta">
          <div className="ac-name">{expert.full_name}</div>
          <div className="ac-role">{role} · {expert.city}</div>
          <div className="ac-badges">
            <span className="badge b-sebi">SEBI aligned</span>
            {expert.is_premium && <span className="badge b-premium">★ Premium</span>}
            {expert.is_verified && <span className="badge b-verified">✓ Verified</span>}
          </div>
        </div>
        <div className="ac-rating">
          <Stars value={avg} />
          <span className="rating-val">{avg.toFixed(1)}</span>
          <span className="rating-cnt">({reviews.length} review{reviews.length !== 1 ? "s" : ""})</span>
        </div>
      </div>

      <div className="ac-stats">
        <div className="ac-stat"><div className="ac-stat-val">{expert.ipo_experience ? `${expert.years_of_experience}+ yrs` : "New"}</div><div className="ac-stat-lbl">IPO experience</div></div>
        <div className="ac-stat"><div className="ac-stat-val">{expert.city}</div><div className="ac-stat-lbl">Based in</div></div>
        <div className="ac-stat"><div className="ac-stat-val">{expert.expertise_labels?.length || 1}</div><div className="ac-stat-lbl">Specialisations</div></div>
        <div className="ac-stat"><div className="ac-stat-val" style={{ color: expert.is_premium ? "#854F0B" : "#185FA5" }}>{expert.is_premium ? "Premium" : "Standard"}</div><div className="ac-stat-lbl">Listing tier</div></div>
      </div>

      {expert.expertise_labels?.length > 1 && (
        <div className="ac-tags">
          {expert.expertise_labels.map((l, i) => <span className="ac-tag" key={i}>{l}</span>)}
        </div>
      )}

      <div className="ac-bottom">
        <div className="ac-desc">
          {expert.full_name} is a {role.toLowerCase()} based in {expert.city}
          {expert.ipo_experience ? ` with ${expert.years_of_experience}+ years across Indian mainboard & SME IPO mandates.` : ", available for new IPO mandates."}
        </div>
        <div className="ac-actions">
          <button className="btn-outline" onClick={() => onToggle(expert.expert_id)} data-testid={`expert-reviews-toggle-${expert.expert_id}`}>
            {expanded ? "Hide reviews" : "View profile & reviews"}
          </button>
          <button className="btn-primary" onClick={() => toast.success(`Contact request noted for ${expert.full_name}`)} data-testid={`expert-contact-${expert.expert_id}`}>Contact ↗</button>
        </div>
      </div>

      {expanded && (
        <div className="reviews-panel" data-testid={`reviews-panel-${expert.expert_id}`}>
          <div className="rv-summary">
            <div className="rv-big">{avg.toFixed(1)}</div>
            <div className="rv-summary-meta">
              <Stars value={avg} className="rv-stars" />
              <div className="rv-date">Based on {reviews.length} client review{reviews.length !== 1 ? "s" : ""}</div>
            </div>
          </div>

          <div className="rv-list">
            {reviews.map((rv, i) => {
              const rc = AV_COLORS[(hashStr(rv.name) + i) % AV_COLORS.length];
              return (
                <div className="rv-item" key={i}>
                  <div className="rv-av" style={{ background: rc.bg, color: rc.c }}>{rv.name.charAt(0).toUpperCase()}</div>
                  <div>
                    <div className="rv-head">
                      <span className="rv-name">{rv.name}</span>
                      <span className="rv-date">{rv.d}</span>
                    </div>
                    <Stars value={rv.r} className="rv-stars" />
                    <div className="rv-text">{rv.t}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rv-form">
            <div className="rv-form-title">Share your experience</div>
            <div className="rv-star-pick" data-testid={`review-star-pick-${expert.expert_id}`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <span key={n} className={`pick ${n <= pick ? "on" : ""}`} onClick={() => setPick(n)} data-testid={`review-star-${expert.expert_id}-${n}`}>★</span>
              ))}
            </div>
            <input className="rv-input" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid={`review-name-${expert.expert_id}`} />
            <textarea className="rv-textarea" placeholder="Write a review…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`review-text-${expert.expert_id}`} />
            <button className="btn-primary" onClick={submit} data-testid={`review-submit-${expert.expert_id}`}>Post review</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdvisorMarketplaceExperts = () => {
  const navigate = useNavigate();
  const [experts, setExperts] = useState([]);
  const [allExperts, setAllExperts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [expertise, setExpertise] = useState("");
  const [city, setCity] = useState("");
  const [premiumOnly, setPremiumOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [userReviews, setUserReviews] = useState({}); // expert_id -> [reviews]

  useEffect(() => {
    const opts = { credentials: "include" };
    fetch(`${API_URL}/api/matchmaker/expert/expertise-areas`, opts).then((r) => r.json()).then((d) => setAreas(d.areas || [])).catch(() => {});
    fetch(`${API_URL}/api/matchmaker/expert/major-cities`, opts).then((r) => r.json()).then((d) => setCities(d.cities || [])).catch(() => {});
    fetch(`${API_URL}/api/matchmaker/experts/browse`, opts).then((r) => r.json()).then((d) => setAllExperts(d.experts || [])).catch(() => {});
  }, []);

  const fetchExperts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (expertise) params.append("expertise", expertise);
      if (city) params.append("city", city);
      if (premiumOnly) params.append("premium_only", "true");
      if (verifiedOnly) params.append("verified_only", "true");
      const res = await fetch(`${API_URL}/api/matchmaker/experts/browse?${params.toString()}`, { credentials: "include" });
      const data = await res.json();
      setExperts(data.experts || []);
    } catch {
      setExperts([]);
    }
    setLoading(false);
  }, [search, expertise, city, premiumOnly, verifiedOnly]);

  useEffect(() => {
    const t = setTimeout(fetchExperts, 250);
    return () => clearTimeout(t);
  }, [fetchExperts]);

  const resolveAreaId = (matchLabel) => {
    if (!matchLabel) return "";
    const a = areas.find((x) => x.label.includes(matchLabel));
    return a ? a.id : "";
  };
  const activeChip = expertise
    ? (CHIP_DEFS.find((c) => c.match && resolveAreaId(c.match) === expertise)?.id || "")
    : "all";

  const clearAll = () => {
    setSearch(""); setExpertise(""); setCity(""); setPremiumOnly(false); setVerifiedOnly(false);
  };
  const hasFilters = search || expertise || city || premiumOnly || verifiedOnly;

  const reviewsFor = (e) => [...(userReviews[e.expert_id] || []), ...seedReviews(e.expert_id)];
  const addReview = (id, review) =>
    setUserReviews((prev) => ({ ...prev, [id]: [review, ...(prev[id] || [])] }));

  const premiumCount = allExperts.filter((e) => e.is_premium).length;
  const verifiedCount = allExperts.filter((e) => e.is_verified).length;
  const cityCount = new Set(allExperts.map((e) => e.city)).size;

  return (
    <div className="adv-mp-root" data-testid="experts-directory-page">
      <div className="mp-topbar">
        <div className="mp-brand" onClick={() => navigate("/advisor-marketplace")} data-testid="exp-dir-brand">
          IPO<span>Navigator</span>
        </div>
        <div className="mp-eyebrow">Experts Directory</div>
        <div className="mp-back" onClick={() => navigate("/advisor-marketplace")} data-testid="exp-dir-back">
          <i className="ti ti-arrow-left" /> Back to Marketplace
        </div>
      </div>

      <div className="mp">
        <h2 className="sr-only">
          Directory of every IPO expert registered on the platform — chartered accountants, legal
          advisors, merchant bankers, registrars, valuation experts and more, with client reviews.
        </h2>

        <div className="mp-header">
          <div className="mp-title">IPO Experts who&apos;ve registered with us</div>
          <div className="mp-sub">
            Browse the complete list of verified IPO professionals on the platform. Filter by role,
            city and listing tier — and read what their clients say.
          </div>

          <div className="stats-row">
            <div className="stat-box"><div className="stat-val" style={{ color: "#185FA5" }}>{allExperts.length}</div><div className="stat-lbl">Registered experts</div></div>
            <div className="stat-box"><div className="stat-val" style={{ color: "#854F0B" }}>{premiumCount}</div><div className="stat-lbl">Premium listings</div></div>
            <div className="stat-box"><div className="stat-val" style={{ color: "#1D9E75" }}>{verifiedCount}</div><div className="stat-lbl">Verified profiles</div></div>
            <div className="stat-box"><div className="stat-val">{cityCount}</div><div className="stat-lbl">Cities covered</div></div>
          </div>

          <div className="filters">
            <div className="search-box">
              <i className="ti ti-search" aria-hidden="true" />
              <input type="text" placeholder="Search by name or city..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="exp-dir-search" />
            </div>
            {CHIP_DEFS.map((c) => (
              <button
                key={c.id}
                className={`filter-chip ${activeChip === c.id ? "on" : ""}`}
                onClick={() => setExpertise(c.match ? resolveAreaId(c.match) : "")}
                data-testid={`exp-chip-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mp-body">
          {/* Left filters */}
          <div className="left-filters">
            <div style={{ paddingTop: 14 }} />
            <div className="lf-section">
              <div className="lf-title">Listing tier</div>
              <div className="lf-item" onClick={() => setPremiumOnly((v) => !v)} data-testid="exp-filter-premium">
                <div className={`lf-check ${premiumOnly ? "on" : ""}`}>{premiumOnly && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                <div className="lf-label">Premium only</div>
              </div>
              <div className="lf-item" onClick={() => setVerifiedOnly((v) => !v)} data-testid="exp-filter-verified">
                <div className={`lf-check ${verifiedOnly ? "on" : ""}`}>{verifiedOnly && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                <div className="lf-label">Verified only</div>
              </div>
            </div>

            <div className="lf-section">
              <div className="lf-title">Expertise area</div>
              {areas.map((a) => (
                <div key={a.id} className="lf-item" onClick={() => setExpertise((v) => (v === a.id ? "" : a.id))} data-testid={`exp-area-${a.id}`}>
                  <div className={`lf-check ${expertise === a.id ? "on" : ""}`}>{expertise === a.id && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                  <div className="lf-label">{a.label}</div>
                </div>
              ))}
            </div>

            <div className="lf-section">
              <div className="lf-title">City</div>
              {cities.slice(0, 14).map((c) => (
                <div key={c} className="lf-item" onClick={() => setCity((v) => (v === c ? "" : c))} data-testid={`exp-city-${c}`}>
                  <div className={`lf-check ${city === c ? "on" : ""}`}>{city === c && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                  <div className="lf-label">{c}</div>
                </div>
              ))}
            </div>

            {hasFilters && <button className="clear-btn" onClick={clearAll} data-testid="exp-clear-filters">Clear all filters ×</button>}
          </div>

          {/* Cards */}
          <div className="cards-area">
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "12px 0 8px" }} data-testid="exp-results-count">
              {loading ? "Loading experts…" : `${experts.length} expert${experts.length !== 1 ? "s" : ""} found`}
            </div>
            {!loading && experts.length === 0 ? (
              <div className="empty-state" data-testid="exp-empty">No experts match your filters. Try clearing some filters.</div>
            ) : (
              experts.map((e, i) => (
                <ExpertCard
                  key={e.expert_id}
                  expert={e}
                  idx={i}
                  expanded={expandedId === e.expert_id}
                  onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
                  reviews={reviewsFor(e)}
                  onAddReview={addReview}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvisorMarketplaceExperts;
